/**
 * Keeps this device's Advertising answer and the account's in step (#5587).
 *
 * The device side is two cookies (`services/ad-consent-cookie.ts`); the account
 * side is the Advertising record `setUserConsent` stores. The newer answer wins,
 * decided by `decideAdConsentSync` in `shared/src/ad-consent.ts`.
 *
 * This lives in a service rather than in the consent dialog so that any screen
 * can save the answer to the account, and so the save-ordering rule below can be
 * tested on its own (`ad-consent.service.test.ts`). Storing the other cookie
 * categories stays in `CookieConsentService`, which is storage only.
 */
import { Injectable } from '@angular/core';
import { UserConsentId, UserConsentRecords } from '@cf-shared/user-consent-interface';
import { AdConsentAnswer, AdConsentSync, decideAdConsentSync, readStoredAdConsentRecord } from '@cf-shared/ad-consent';
import { readAdConsentCookies, resolveEffectiveAdConsent, writeAdConsentCookies } from 'services/ad-consent-cookie';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { CookieConsentService } from 'services/cookie-consent.service';
import { adConsentChangedEvent, PubSubService } from 'services/pubsub.service';
import { isNetworkError } from 'services/cloud-functions-errors';
import { AppVersion } from 'app/version';

@Injectable({ providedIn: 'root' })
export class AdConsentService {
  /**
   * Which Advertising save started most recently. A save's reply carries the
   * answer THAT call sent, whatever has happened since, so a reply is only
   * applied while it is still the newest save — two answers given inside one
   * network round trip would otherwise end with the older one on the device.
   */
  private saveSequence = 0;

  constructor(
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private cookieConsent: CookieConsentService,
    private pubSub: PubSubService
  ) {}

  /** Line the device and the account up: the newer of the two answers wins. */
  syncWithAccount(consents: UserConsentRecords) {
    const device = readAdConsentCookies();
    const account = readStoredAdConsentRecord(consents);
    switch (decideAdConsentSync(device, account)) {
      case AdConsentSync.CopyAccountToDevice:
        this.copyAccountToDevice(consents);
        break;
      case AdConsentSync.SendDeviceToAccount:
        void this.saveToAccount(device.answer);
        break;
      case AdConsentSync.Nothing:
        break;
    }
  }

  /**
   * Take a place in the save sequence for a save made somewhere else — the Terms
   * screen saves the Advertising answer inside its own, bigger `setUserConsent`
   * call. Hand the number back to {@link applySaveReplyIfLatest} with its reply.
   */
  beginSave(): number {
    return ++this.saveSequence;
  }

  /**
   * Apply a save's reply to the device, unless a newer save has started since —
   * that newer save's own reply then speaks for the device. The device gets the
   * server's time, so the next sign-in compares equal instead of comparing two
   * different clocks.
   */
  applySaveReplyIfLatest(sequence: number, consents: UserConsentRecords | undefined) {
    if (sequence !== this.saveSequence) return;
    this.copyAccountToDevice(consents);
  }

  /** Save this device's Advertising answer on the account, leaving every other
   *  consent record alone. */
  async saveToAccount(answer: AdConsentAnswer) {
    const sequence = this.beginSave();
    try {
      const res = await this.cloudFunctions.setUserConsent({
        consents: { [UserConsentId.Advertising]: answer === AdConsentAnswer.Yes },
        appVersion: AppVersion,
        onlyOptional: true,
      });
      if (res.error) {
        this.auth.logging.trackSilentProblem('adConsentSaveFailed', { message: res.error });
        return;
      }
      // a newer answer was saved while this call was in flight: its own reply
      // carries the truth, so leave everything to it
      if (sequence !== this.saveSequence) return;
      // a sign-out during the call nulls auth.couch
      if (res.consentRecords && this.auth.couch) {
        this.auth.couch.consents = { ...res.consentRecords };
      }
      this.applySaveReplyIfLatest(sequence, res.consentRecords);
    } catch (err) {
      // offline is expected. Nothing is lost: the device keeps the newer answer,
      // and the next sign-in sends it again.
      if (isNetworkError(err)) return;
      this.auth.logging.trackSilentProblem('adConsentSaveFailed', { code: err?.code, message: err?.message });
    }
  }

  /**
   * Write the account's Advertising answer onto this device, with the ACCOUNT's
   * time. A caller applying a save's reply goes through
   * {@link applySaveReplyIfLatest} instead: a reply to a superseded save carries
   * the older answer, and writing it here would undo the newer one.
   */
  private copyAccountToDevice(consents: UserConsentRecords | undefined) {
    const account = readStoredAdConsentRecord(consents);
    if (!account) return;
    const device = readAdConsentCookies();
    if (device?.answer === account.answer && device.at === account.at) return;
    // no fabricated time when the account record carries none: an invented "now"
    // would read as an answer given AFTER the Global Privacy Control signal
    // (#5663) and silently override it. Undated is the honest record, and
    // readDeviceAdConsent already knows what to do with it.
    writeAdConsentCookies(account.answer, account.at);
    // read the answer back rather than using the account's directly: a browser
    // sending the Global Privacy Control signal (#5663) must not have the toggle
    // flipped on by a sign-in that merely copied an older "yes" across. Mapped the
    // same way readFromCookies maps it (#5712): no answer is null, not false, or
    // this writer would draw the toggle OFF where the other draws it neutral and
    // let a "no" nobody chose be saved
    const effective = resolveEffectiveAdConsent();
    this.cookieConsent.values.ad = effective === undefined ? null : effective === AdConsentAnswer.Yes;
    this.pubSub.publish(adConsentChangedEvent, undefined);
  }
}
